import { useEffect, useCallback, useRef, useState } from 'react';
import { MIDASProject, SortCondition, getFromDatasetRecord, createDatasetId } from '../../../types';
import { usePaneLayout } from '../../stores/hooks/usePaneLayout';
import { useDragOverlay } from '../../hooks/useDragOverlay';
import { useProjectReportManager } from '../../hooks/useProjectReportManager';
// Web版のプロジェクト永続化フックを使用
import { useProjectPersistence } from '../../hooks/useProjectPersistenceWeb';
import { useTabOperations } from '../../hooks/useTabOperations';
import { useProjectStore } from '../../stores/projectStore';
import { getDatasetSelection } from '../../utils/projectUtils';
import { findActiveTab } from '../../utils/paneUtils';
import logger from '../../utils/logger';
import type { TabCreationComboBoxRef } from '../TabCreationComboBox';

/**
 * ProjectViewの状態管理を担当するカスタムフック
 * すべての状態とロジックをここに集約
 */
export function useProjectViewStates(projectId: string) {
  // ProjectStoreからprojectを取得
  const project = useProjectStore((state) => state.project);
  
  // 基本的な状態
  // プロジェクトが既に存在する場合はisLoadingをfalseに
  const [isLoading, setIsLoading] = useState(!project);
  const [error, setError] = useState<string | null>(null);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  
  // TabCreationComboBoxへの参照
  const tabCreationComboBoxRef = useRef<TabCreationComboBoxRef>(null);
  
  // ペインレイアウト管理
  const { 
    layout, 
    activateTab, 
    closeTab, 
    addTab,
    addTabWithOptions,
    updateLayout, 
    startDrag, 
    endDrag, 
    moveTabWithinPane,
    moveTabToPane,
    handleDropZone,
    findTab
  } = usePaneLayout();

  // ドラッグオーバーレイ管理
  const { 
    dragState,
    isDragging,
    startDrag: startDragOverlay,
    endDrag: endDragOverlay
  } = useDragOverlay();
  
  // 現在のアクティブなデータセットの選択状態を取得するヘルパー関数
  const getCurrentDatasetSelection = useCallback((datasetId?: string) => {
    if (!datasetId || !project) return { selectedColumns: [], selectedRows: [] };
    return getDatasetSelection(project, datasetId);
  }, [project]);

  // 最後にアクティブだったデータテーブルタブのデータセットIDを保持
  const [lastActiveDataTableDatasetId, setLastActiveDataTableDatasetId] = useState<string | null>(null);
  
  // アクティブなタブが変わった時、データテーブルタブならそのデータセットIDを記録
  useEffect(() => {
    const activeTab = layout.activeContainerId ? findActiveTab(layout.rootPane, layout.activeContainerId) : null;
    if (activeTab?.type === 'data-table' && activeTab.metadata?.datasetId) {
      setLastActiveDataTableDatasetId(activeTab.metadata.datasetId);
    }
  }, [layout]);

  // 現在のアクティブなデータセットを取得するヘルパー関数
  const getActiveDataset = useCallback(() => {
    if (!project || !project.datasets) {
      return null;
    }
    
    // アクティブなタブを探す
    const activeTab = layout.activeContainerId ? findActiveTab(layout.rootPane, layout.activeContainerId) : null;
    
    // アクティブなタブがデータテーブルタブの場合、そのデータセットを返す
    if (activeTab?.type === 'data-table' && activeTab.metadata?.datasetId) {
      const dataset = project.datasets[activeTab.metadata.datasetId];
      if (!dataset) {
        logger.warn('[getActiveDataset] Dataset not found in project:', {
          datasetId: activeTab.metadata.datasetId,
          availableDatasets: Object.keys(project.datasets)
        });
      }
      return dataset || null;
    }
    
    // アクティブなタブが統計タブの場合、targetDatasetIdがあればそれを使用
    if (activeTab?.type === 'statistics' && activeTab.metadata?.targetDatasetId) {
      return project.datasets[activeTab.metadata.targetDatasetId] || null;
    }
    
    // それ以外の場合は、最後にアクティブだったデータテーブルタブのデータセットを使用
    const lastActiveDataset = lastActiveDataTableDatasetId ? getFromDatasetRecord(project.datasets, createDatasetId(lastActiveDataTableDatasetId)) : undefined;
    if (lastActiveDataset) {
      return lastActiveDataset;
    }
    
    // フォールバック: 最初のデータセットを返す
    const datasetIds = Object.keys(project.datasets);
    if (datasetIds.length > 0) {
      return getFromDatasetRecord(project.datasets, createDatasetId(datasetIds[0])) || null;
    }
    
    return null;
  }, [project, layout, lastActiveDataTableDatasetId]);

  // 現在のアクティブなデータセットの選択状態を取得
  const getCurrentActiveDatasetSelection = useCallback(() => {
    const activeDataset = getActiveDataset();
    if (!activeDataset) {
      return { selectedColumns: [], selectedRows: [] };
    }
    
    const selection = getCurrentDatasetSelection(activeDataset.id);
    
    return selection;
  }, [getActiveDataset, getCurrentDatasetSelection]);

  // ソート条件を取得（現在は空配列を返す）
  const getCurrentSortConditions = useCallback((): SortCondition[] => {
    return [];
  }, []);

  // レポート管理フック
  const reportManager = useProjectReportManager({
    project,
    onProjectUpdate: (updatedProject: MIDASProject) => {
      useProjectStore.getState().loadProject(updatedProject);
    }
  });

  // プロジェクト永続化の管理
  const persistenceManager = useProjectPersistence({
    projectId,
    project,
    layout: {
      rootPane: layout.rootPane,
      activeContainerId: layout.activeContainerId ?? null
    },
    getCurrentActiveDatasetSelection,
    getCurrentSortConditions,
    getActiveDataset,
    onProjectUpdate: (updatedProject: MIDASProject) => {
      useProjectStore.getState().loadProject(updatedProject);
    }
  });

  // タブ操作の管理
  const tabOperations = useTabOperations({
    project,
    layout: {
      rootPane: layout.rootPane,
      activeContainerId: layout.activeContainerId ?? null
    },
    addTab,
    addTabWithOptions,
    closeTab,
    moveTabWithinPane,
    moveTabToPane,
    startDrag: startDragOverlay,
    endDrag: endDragOverlay
  });

  // プロジェクトが読み込まれたらisLoadingをfalseに
  useEffect(() => {
    if (project) {
      setIsLoading(false);
    }
  }, [project]);

  return {
    // 基本的な状態
    isLoading,
    setIsLoading,
    error,
    setError,
    isErrorDialogOpen,
    setIsErrorDialogOpen,
    project,
    tabCreationComboBoxRef,
    
    // レイアウト関連
    layout: {
      rootPane: layout.rootPane,
      activeContainerId: layout.activeContainerId ?? null
    },
    activateTab,
    closeTab,
    addTab,
    addTabWithOptions,
    updateLayout,
    startDrag,
    endDrag,
    moveTabWithinPane,
    moveTabToPane,
    handleDropZone,
    findTab,
    
    // ドラッグオーバーレイ
    dragState,
    isDragging,
    startDragOverlay,
    endDragOverlay,
    
    // データセット関連
    getActiveDataset,
    getCurrentActiveDatasetSelection,
    getCurrentSortConditions,
    lastActiveDataTableDatasetId,
    
    // レポート管理
    reportManager,
    
    // 永続化管理
    persistenceManager,
    
    // タブ操作
    tabOperations,
    
    // 内部で使用するprojectId
    projectId
  };
}